import { Avatar, Badge, DetailTemplate, StatsRow } from '@room/shared-ui';
import { Button } from '@room/shared-ui/components/ui/button';
import type { ReactNode } from 'react';

type PluginType = 'bridge' | 'game' | 'tool' | 'utility';

export type PluginRecord = {
  id: string;
  type?: PluginType;
  name?: string;
  description?: string;
  version?: string;
  status?: string;
  publishStatus?: string;
  owned?: boolean;
  installs?: number;
  active_users?: number;
  enabled?: boolean;
  permissions?: string[];
  userState?: 'enabled' | 'disabled';
  ui?: Record<string, unknown>;
  entry?: Record<string, unknown>;
  manifest?: {
    type?: PluginType;
    ui?: {
      sidebar?: {
        icon?: string;
      };
    };
  };
};

type PluginDetailProps = {
  plugin: PluginRecord | null;
  icon: ReactNode;
  typeLabel?: string;
  onBack?: () => void;
  showBackButton?: boolean;
};

const getPrimaryActionLabel = (plugin: PluginRecord): string => {
  if (plugin.userState === 'enabled' || plugin.enabled) {
    return 'Open';
  }
  return 'Install';
};

export function PluginDetail({
  plugin,
  icon,
  typeLabel,
  onBack,
  showBackButton = false,
}: PluginDetailProps) {
  if (!plugin) {
    return (
      <div className="empty-state">
        <div className="empty-icon">🧭</div>
        <p>Select a plugin to view details.</p>
      </div>
    );
  }

  const actionLabel = getPrimaryActionLabel(plugin);
  const permissions = plugin.permissions ?? [];
  const installs = plugin.installs ?? 0;
  const activeUsers = plugin.active_users ?? 0;

  return (
    <DetailTemplate
      header={(
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 'var(--space-3)',
          }}
        >
          {showBackButton && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onBack}
              aria-label="Back to store list"
            >
              ←
            </Button>
          )}
          <Avatar shape="square" size="lg" icon={icon} />
          <div style={{ minWidth: 0 }}>
            <h2 style={{ margin: 0, fontSize: 'var(--font-size-lg)' }}>
              {plugin.name ?? plugin.id}
            </h2>
            <p
              style={{
                margin: '4px 0 0',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-text-secondary)',
              }}
            >
              {typeLabel ?? 'Plugin'} · {plugin.version ?? '—'}
            </p>
          </div>
        </div>
      )}
      actions={(
        <Button
          variant="primary"
          size="sm"
          onClick={() => console.log(`${actionLabel} plugin`, plugin.id)}
        >
          {actionLabel}
        </Button>
      )}
      sections={[
        {
          title: 'Overview',
          content: (
            <p style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
              {plugin.description ?? 'No description provided yet.'}
            </p>
          ),
        },
        {
          title: 'Usage',
          content: (
            <StatsRow
              items={[
                { label: 'Installs', value: installs.toLocaleString() },
                { label: 'Active users', value: activeUsers.toLocaleString() },
                { label: 'Status', value: plugin.publishStatus ?? '—' },
              ]}
            />
          ),
        },
        {
          title: 'Permissions',
          content: permissions.length > 0 ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
              {permissions.map((permission) => (
                <Badge key={permission} size="sm" variant="default">
                  {permission}
                </Badge>
              ))}
            </div>
          ) : (
            <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
              No special permissions required.
            </p>
          ),
        },
      ]}
    />
  );
}

export default PluginDetail;
